import React, {useMemo} from 'react';
import {
    Box,
    Drawer,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Divider,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type {AdjustmentDetail} from '@/services/auditService/interface/wp-row';
import type {AuditAdjustHeader, AuditAdjustLine} from '@/services/auditService/interface/adjust-orchestrate';
import {MoneyField} from '@/components/common';
import {useFsAdjustStore, useFsCategoryStore} from "@/store/audit";

/**
 * Determine Dr/Cr placement based on FS entry and adjustment amount.
 * - entry=Dr, amount>0 → Dr column
 * - entry=Dr, amount<0 → Cr column
 * - entry=Cr, amount>0 → Cr column
 * - entry=Cr, amount<0 → Dr column
 */
function placeDrCr(entry: string | null | undefined, amount: number): { dr: number; cr: number } {
    if (!amount) return {dr: 0, cr: 0};
    const isEntryDr = String(entry || '').toLowerCase().startsWith('dr');
    return isEntryDr ? {dr: Math.abs(amount), cr: 0} : {dr: 0, cr: Math.abs(amount)};
}

export interface AdjustmentDetailsDrawerProps {
    open: boolean;
    onClose: () => void;
    itemName?: string;
    adjustments?: AdjustmentDetail[];
    currency: string;
    /** Header-data mode: when provided, renders FS line details with Dr/Cr based on entry */
    headerData?: AuditAdjustHeader[] | null;
    /** Whether header data is loading */
    headerDataLoading?: boolean;
}

const fmt = (n: number) =>
    Math.abs(n).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});

export function AdjustmentDetailsDrawer({
    open,
    onClose,
    itemName,
    adjustments,
    currency,
    headerData,
    headerDataLoading,
}: AdjustmentDetailsDrawerProps) {
    const byCode = useFsAdjustStore((s) => s.byCode);
    const catByCode = useFsCategoryStore((s) => s.byCode);
    const useHeaderMode = !!headerData || headerDataLoading;

    // --- Header-data mode: flatten lines with FS lookup ---
    const headerRows = useMemo(() => {
        if (!headerData) return [];
        const rows: { key: string; refNo: string; name: string; detailName: string; dr: number; cr: number }[] = [];
        headerData.forEach((header) => {
            const refNo = header.refNo || '-';
            (header.lines || []).forEach((line: AuditAdjustLine, li: number) => {
                const code = line.code || '';
                const fsLine = code ? byCode[code] : null;
                const catLine = code ? (catByCode[code] || catByCode[code.toUpperCase()]) : null;
                const name = code.toUpperCase() === 'PL-TAX' ? 'Taxation'
                    : (fsLine?.name || catLine?.name || code || '-');
                // 优先使用 line.entry（来自后端返回的调整数据），fallback 到 fsLine.entry
                const entry = line.entry ?? fsLine?.entry ?? null;

                // 从 details 中提取每个 row 的 adjustment 值
                // 兼容所有 wp-row.ts 数据结构类型：
                //   - row.adjustments: AdjustmentDetail[]（后端返回的多条调整记录）
                //   - row.adjustment: number（单值，来自用户编辑或当前保存操作）
                // details 结构: [{ commonRow: [...], dueFromToDirRow: [...], bankRow: [...], ... }]
                const details = line.details;
                let rowIndex = 0;
                if (Array.isArray(details)) {
                    details.forEach((detailObj: any) => {
                        if (!detailObj || typeof detailObj !== 'object') return;
                        // 遍历 detailObj 的所有 key（如 commonRow, dueFromToDirRow, bankRow, salesIncome 等）
                        Object.keys(detailObj).forEach((rowKey) => {
                            const rowArray = detailObj[rowKey];
                            if (!Array.isArray(rowArray)) return;
                            rowArray.forEach((row: any, ri: number) => {
                                if (!row || typeof row !== 'object') return;
                                // 提取行显示名称：兼容所有 wp-row.ts 接口类型
                                const rowDisplayName = row.item            // CommonRow, DisposalRow
                                    || row.directorName                    // DueFromToDirRow, DirectorSalaryRow, DirectorQuarterRow
                                    || row.bankName                        // BankRow
                                    || row.supplierName                    // CommonFeesRow
                                    || row.customer                        // CommonDebtRow
                                    || row.commissionPaymentName           // CommissionPaymentRow
                                    || row.name                            // StaffSalaryRow, other
                                    || '';

                                const adjustmentsArr = row.adjustments;
                                if (Array.isArray(adjustmentsArr) && adjustmentsArr.length > 0) {
                                    // 方式1：后端返回了多条调整记录（adjustments 数组）
                                    adjustmentsArr.forEach((adj: any, ai: number) => {
                                        const adjValue = Number(adj?.adjustment) || 0;
                                        if (adjValue === 0) return; // 跳过零值
                                        const {dr, cr} = placeDrCr(entry, adjValue);
                                        rows.push({
                                            key: `${header.id}-${line.id ?? li}-${rowKey}-${ri}-${ai}`,
                                            refNo,
                                            name,
                                            detailName: rowDisplayName,
                                            dr,
                                            cr
                                        });
                                        rowIndex++;
                                    });
                                } else {
                                    // 方式2：单值 adjustment（来自用户编辑或当前保存操作）
                                    const adjValue = Number(row.adjustment) || 0;
                                    if (adjValue === 0) return; // 跳过零值
                                    const {dr, cr} = placeDrCr(entry, adjValue);
                                    rows.push({
                                        key: `${header.id}-${line.id ?? li}-${rowKey}-${ri}`,
                                        refNo,
                                        name,
                                        detailName: rowDisplayName,
                                        dr,
                                        cr
                                    });
                                    rowIndex++;
                                }
                            });
                        });
                    });
                }

                // 如果没有从 details 中提取到任何 adjustment，fallback 到 line.adjustmentAmount
                if (rowIndex === 0) {
                    const amount = Number(line.adjustmentAmount) || 0;
                    if (amount !== 0) {
                        const {dr, cr} = placeDrCr(entry, amount);
                        rows.push({key: `${header.id}-${line.id ?? li}`, refNo, name, detailName: '', dr, cr});
                    }
                }
            });
        });
        // Dr有值的行排在顶部
        rows.sort((a, b) => (b.dr !== 0 ? 1 : 0) - (a.dr !== 0 ? 1 : 0));
        return rows;
    }, [headerData, byCode, catByCode]);

    // --- Render helpers ---
    const renderHeaderModeBody = () => {
        if (headerDataLoading) {
            return (
                <TableRow>
                    <TableCell colSpan={3} align="center">
                        <Box sx={{py: 3, display: 'flex', justifyContent: 'center'}}>
                            <CircularProgress size={24}/>
                        </Box>
                    </TableCell>
                </TableRow>
            );
        }
        if (headerRows.length === 0) {
            return (
                <TableRow>
                    <TableCell colSpan={3} align="center">
                        <Box sx={{py: 3}}>
                            <Typography variant="body2" color="text.secondary">No adjustments yet</Typography>
                        </Box>
                    </TableCell>
                </TableRow>
            );
        }
        // Group by header refNo
        const grouped: Record<string, typeof headerRows> = {};
        headerRows.forEach((r) => {
            if (!grouped[r.refNo]) grouped[r.refNo] = [];
            grouped[r.refNo].push(r);
        });
        return Object.entries(grouped).map(([refNo, lines]) => (
            <React.Fragment key={refNo}>
                {/* Group header row */}
                <TableRow>
                    <TableCell colSpan={3} sx={{bgcolor: 'action.hover', py: 0.5}}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                            {/*Ref No. {refNo}*/}
                        </Typography>
                    </TableCell>
                </TableRow>
                {lines.map((row) => (
                    <TableRow key={row.key} hover>
                        <TableCell>
                            <Typography variant="body2" fontWeight={700}>
                                {/*{row.detailName ? `${row.detailName} (${row.name})` : row.name}*/}
                                {row.name}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            {row.dr !== 0 ? (
                                <MoneyField
                                    value={Math.abs(row.dr)}
                                    showCents
                                    align="center"
                                    compact
                                    currencySymbol={currency}
                                />
                            ) : (
                                <Typography variant="body2" color="text.disabled">-</Typography>
                            )}
                        </TableCell>
                        <TableCell align="center">
                            {row.cr !== 0 ? (
                                <MoneyField
                                    value={Math.abs(row.cr)}
                                    showCents
                                    align="center"
                                    compact
                                    currencySymbol={currency}
                                />
                            ) : (
                                <Typography variant="body2" color="text.disabled">-</Typography>
                            )}
                        </TableCell>
                    </TableRow>
                ))}
            </React.Fragment>
        ));
    };

    const renderSimpleModeBody = () => {
        // Dr有值的行排在顶部
        const sorted = [...(adjustments || [])].sort((a, b) => {
            const aDr = (Number(a.adjustment) || 0) > 0 ? 0 : 1;
            const bDr = (Number(b.adjustment) || 0) > 0 ? 0 : 1;
            return aDr - bDr;
        });
        return (
        <>
            {sorted.map((detail, idx) => {
                const val = Number(detail.adjustment) || 0;
                const isDr = val > 0;
                return (
                    <TableRow key={detail.adjustHeaderId ?? idx} hover>
                        <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                                {detail.refNo || '-'}
                            </Typography>
                        </TableCell>
                        <TableCell align="right">
                            {isDr ? (
                                <Typography variant="body2" color="success.main">{fmt(val)}</Typography>
                            ) : (
                                <Typography variant="body2" color="text.disabled">-</Typography>
                            )}
                        </TableCell>
                        <TableCell align="right">
                            {!isDr && val !== 0 ? (
                                <Typography variant="body2" color="error.main">{fmt(Math.abs(val))}</Typography>
                            ) : (
                                <Typography variant="body2" color="text.disabled">-</Typography>
                            )}
                        </TableCell>
                    </TableRow>
                );
            })}
            {(!adjustments || adjustments.length === 0) && (
                <TableRow>
                    <TableCell colSpan={3} align="center">
                        <Box sx={{py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1}}>
                            <Typography variant="body2" color="text.secondary">No adjustments yet</Typography>
                        </Box>
                    </TableCell>
                </TableRow>
            )}
        </>
        );
    };

    return (
        <Drawer
            anchor="left"
            open={open}
            onClose={onClose}
            PaperProps={{sx: {width: {xs: '100%', sm: 480}, p: 0}}}
            sx={(theme) => ({
                zIndex: theme.zIndex.modal + 2,
                position: 'relative',
            })}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2.5,
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Adjustment Details
                    </Typography>
                    {itemName && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {itemName}
                        </Typography>
                    )}
                </Box>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon fontSize="small"/>
                </IconButton>
            </Box>

            <Divider/>

            {/* Table */}
            <TableContainer sx={{flex: 1, overflow: 'auto', px: 1}}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{fontWeight: 600, minWidth: useHeaderMode ? 160 : 60}}>
                                {useHeaderMode ? '' : 'Ref No.'}
                            </TableCell>
                            <TableCell align="center" sx={{fontWeight: 600, minWidth: 110}}>
                                Dr.
                            </TableCell>
                            <TableCell align="center" sx={{fontWeight: 600, minWidth: 110}}>
                                Cr.
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {useHeaderMode ? renderHeaderModeBody() : renderSimpleModeBody()}
                    </TableBody>
                </Table>
            </TableContainer>

        </Drawer>
    );
}
